// ./src/utils/api/RsvpQuestionAnswerService.ts
//
// A guest's answers to an event's registration questions. Mirrors the
// eventsmagic-api RsvpQuestionAnswer SDL. One row per (question, guest); the
// answer_* field matching the question's valueType is the one that's used. Used
// by the guest editor to view / fill answers on a guest's behalf.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

const FIELDS = `
  id
  rsvpQuestionId
  eventId
  eventGuestId
  answerText
  answerNumber
  answerBoolean
  answerDate
  answerJson
  answerImageFile { id fileUrl thumbnailUrl }
  answeredAt
  status
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query RsvpQuestionAnswerList($filter: RsvpQuestionAnswerFilter, $params: PaginationAndSorting) {
    rsvpQuestionAnswerList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation RsvpQuestionAnswerCreate($params: RsvpQuestionAnswerInput) {
    rsvpQuestionAnswerCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation RsvpQuestionAnswerUpdate($where: RsvpQuestionAnswerWhereInput, $params: RsvpQuestionAnswerInput) {
    rsvpQuestionAnswerUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation RsvpQuestionAnswerRemove($where: RsvpQuestionAnswerWhereInput) {
    rsvpQuestionAnswerRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

/**
 * ROADMAP #172 — one person's answers with the questions they answer, read at the door. The door's own read: it works for
 * crew signed in with the event's crew password, and asks only `guests.view` (the CRUD reads need an account, and the
 * questions need `event.operate`). Read-only.
 */
const SHEET_QUERY = gql`
  query RsvpQuestionAnswerSheet($params: RsvpQuestionAnswerSheetInput) {
    rsvpQuestionAnswerSheet(params: $params) {
      code
      data {
        eventGuestId
        questions { id title helpText questionType valueType personField orderNo options { id label optionValue } }
        answers { ${FIELDS} }
      }
      errors { name errors warnings }
    }
  }
`;

export interface RsvpQuestionAnswer {
  id?: string;
  rsvpQuestionId?: string | null;
  eventId?: string | null;
  eventGuestId?: string | null;
  answerText?: string | null;
  answerNumber?: number | null;
  answerBoolean?: boolean | null;
  answerDate?: string | null;
  answerJson?: unknown;
  /** A2e #91 — a picture question's answer: the picture. Null for every other answer. */
  answerImageFile?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  answeredAt?: string | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface RsvpQuestionAnswerInput {
  rsvpQuestionId?: string | null;
  eventGuestId?: string | null;
  answerText?: string | null;
  answerNumber?: number | null;
  answerBoolean?: boolean | null;
  answerDate?: string | null;
  answerJson?: unknown;
  answeredAt?: string | null;
}

/**
 * One page of answers. The class has no public `list` — every read is keyed on its subject (see below) — so the
 * whole-set reads page through this. ROADMAP #118: the three per-subject reads asked for one page of 500 and a guest
 * with more answers (a long form, a big party answering per attendee) lost the rest without a word.
 */
const listAnswers = (filter?: Record<string, unknown>, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> =>
  ApiService.executeQuery(LIST_QUERY, {
    filter: filter || {},
    params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
  });

export class RsvpQuestionAnswerService {
  /**
   * All of one PARTNER APPLICATION's answers.
   *
   * ⚠ Keyed on the application, never on the question alone. One table holds both a guest's answers
   * and an applicant's (two nullable refs, exactly one set), so a lookup that omits the subject
   * returns somebody else's answers.
   */
  static async listForApplication(partnerApplicationId: string): Promise<OpResult> {
    return listAllPages<RsvpQuestionAnswer>(listAnswers, { partnerApplicationId: [partnerApplicationId] });
  }

  /** All of one VOLUNTEER APPLICATION's answers (#A4.6) — keyed on the application, for the same reason. */
  static async listForVolunteerApplication(volunteerApplicationId: string): Promise<OpResult> {
    return listAllPages<RsvpQuestionAnswer>(listAnswers, { volunteerApplicationId: [volunteerApplicationId] });
  }

  /** #172 — one person's answers and the event's questions, read-only (the door). `data[0]` is the sheet. */
  static async sheet(eventId: string, eventGuestId: string): Promise<OpResult> {
    return ApiService.executeQuery(SHEET_QUERY, { params: { eventId, eventGuestId } });
  }

  /** All of one guest's answers. */
  static async listForGuest(eventGuestId: string): Promise<OpResult> {
    return listAllPages<RsvpQuestionAnswer>(listAnswers, { eventGuestId: [eventGuestId] });
  }

  /**
   * Every answer for an event (all guests × all questions), paged through — for the guest list's "Answers" matrix
   * view. Read WHOLE (ROADMAP #118): it used to stop at a silent 50,000; `listAllPages` refuses past that instead.
   */
  static async listForEvent(eventId: string): Promise<OpResult> {
    return listAllPages<RsvpQuestionAnswer>(listAnswers, { eventId: [eventId] });
  }

  static async create(params: RsvpQuestionAnswerInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(id: string, params: RsvpQuestionAnswerInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
